import { Button } from "@fluentui/react-components";
import { ChevronLeft20Filled } from "@fluentui/react-icons";
import React from "react";
import { useNavigate } from "react-router";
import errorIcon from "../../images/error_icon.png";
import styles from "./index.module.css";

type ErrorDisplayType =
  | "notFound"
  | "permissionError"
  | "notFoundOrPermissionError"
  | "invitedOrganization";

type ButtonType = {
  title: string;
  link: string;
  replace: boolean;
};

const wordsMap: Record<
  Exclude<ErrorDisplayType, "invitedOrganization">,
  {
    header: string;
    content: string;
    buttons?: ButtonType[];
  }
> = {
  notFound: {
    header: "ご指定のページが見つかりません。",
    content:
      "URLが間違っているか、ページが削除された可能性があります。お手数ですが、URLに間違いはないかご確認ください。",
  },
  permissionError: {
    header: "ご指定のページへアクセスする権限がありません。",
    content: "アクセス権限の詳細については、組織管理者へお問い合わせください。",
  },
  notFoundOrPermissionError: {
    header: "ご指定のページは表示できません。",
    content:
      "適切なアクセス権がないか、対象のシートが存在しません。詳細については、組織管理者へお問い合わせください。",
  },
};

type ErrorDisplayProps =
  | {
      errorDisplayType: "notFound";
      missingPermissions?: never;
      invitedOrgInfo?: never;
    }
  | {
      errorDisplayType: "permissionError";
      missingPermissions: string[];
      invitedOrgInfo?: never;
    }
  | {
      errorDisplayType: "notFoundOrPermissionError";
      missingPermissions: string[];
      invitedOrgInfo?: never;
    }
  | {
      errorDisplayType: "invitedOrganization";
      missingPermissions?: never;
      invitedOrgInfo: {
        organizationId: string;
        organizationName: string;
      };
    };

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  errorDisplayType,
  missingPermissions,
  invitedOrgInfo,
}) => {
  const displayContent = React.useMemo(() => {
    if (errorDisplayType === "invitedOrganization") {
      const orgId = encodeURIComponent(invitedOrgInfo.organizationId);
      return {
        header: `「${invitedOrgInfo.organizationName}」に招待されています`,
        content:
          "以下のボタンを押すと組織への参加が完了し、この組織でOPTIUMをご利用いただけるようになります。",
        buttons: [
          {
            title: "組織に参加する",
            link: `/organizations?tab=invited&accept_org_invitation=${orgId}`,
            replace: false,
          },
        ],
      };
    }
    return wordsMap[errorDisplayType];
  }, [errorDisplayType, invitedOrgInfo]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.errorInfoContainer}>
          <div className={styles.errorInfoHeading}>
            <img src={errorIcon} alt="ErrorIcon" width={20} height={20} />
            {displayContent.header}
          </div>
          <div className={styles.errorInfoDescription}>
            {errorDisplayType === "permissionError" &&
              missingPermissions &&
              missingPermissions.length > 0 && (
                <div>
                  <div>以下の権限が必要です。</div>
                  <ul className={styles.missingPermissionsList}>
                    {missingPermissions.map((permission) => (
                      <li key={permission}>{permission}</li>
                    ))}
                  </ul>
                </div>
              )}
            {displayContent.content}
          </div>
          <ButtonsToRecover buttons={displayContent.buttons} />
        </div>
      </div>
    </>
  );
};

const ButtonsToRecover: React.FC<{
  buttons?: ButtonType[];
}> = ({ buttons }) => {
  const navigate = useNavigate();
  return (
    <div className={styles.buttonContainer}>
      {buttons ? (
        buttons.map((button) => (
          <Button
            appearance="primary"
            key={button.link}
            onClick={() => navigate(button.link, { replace: button.replace })}
          >
            {button.title}
          </Button>
        ))
      ) : (
        <>
          <Button
            appearance="primary"
            onClick={() => navigate("/organizations", { replace: true })}
          >
            組織一覧へ
          </Button>
          <Button icon={<ChevronLeft20Filled />} onClick={() => navigate(-1)}>
            前の画面へ戻る
          </Button>
        </>
      )}
    </div>
  );
};
